import { Professional } from '@/hooks/useSearch';
import { haversineKm } from './geo';

export type Coords = { lat: number; lng: number };

export type ProfessionalWithCoords = Professional & {
  validCoords: Coords;
};

function getMedian(values: number[]): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

/** Excluye pins alejados del grueso de resultados (coords mal geocodificadas). */
export function filterMapOutliers(
  items: ProfessionalWithCoords[],
  maxDelta = 0.75
): ProfessionalWithCoords[] {
  if (items.length <= 2) return items;

  const medianLat = getMedian(items.map((item) => item.validCoords.lat));
  const medianLng = getMedian(items.map((item) => item.validCoords.lng));

  const filtered = items.filter(
    (item) =>
      Math.abs(item.validCoords.lat - medianLat) <= maxDelta &&
      Math.abs(item.validCoords.lng - medianLng) <= maxDelta
  );

  if (filtered.length >= Math.max(3, Math.ceil(items.length * 0.6))) {
    return filtered;
  }

  return items;
}

export function getMapClusterCenter(items: ProfessionalWithCoords[]): Coords | null {
  if (items.length === 0) return null;

  return {
    lat: getMedian(items.map((item) => item.validCoords.lat)),
    lng: getMedian(items.map((item) => item.validCoords.lng)),
  };
}

export function shouldUseFixedCityView(items: ProfessionalWithCoords[]): boolean {
  if (items.length === 0) return false;
  if (items.length > 40) return true;

  const lats = items.map((item) => item.validCoords.lat);
  const lngs = items.map((item) => item.validCoords.lng);
  const latSpread = Math.max(...lats) - Math.min(...lats);
  const lngSpread = Math.max(...lngs) - Math.min(...lngs);

  return latSpread > 0.45 || lngSpread > 0.45;
}

export function clampMapZoom(map: google.maps.Map, minZoom = 11, maxZoom = 14) {
  const zoom = map.getZoom();
  if (zoom == null) return;

  if (zoom < minZoom) map.setZoom(minZoom);
  if (zoom > maxZoom) map.setZoom(maxZoom);
}

function squaredDistance(a: Coords, b: Coords): number {
  const dLat = a.lat - b.lat;
  const dLng = a.lng - b.lng;
  return dLat * dLat + dLng * dLng;
}

/**
 * Tope para que el mapa intente "alcanzar" un resultado con sus bordes. Con
 * el fallback a nivel país (VEN-419), lo más cercano puede estar a cientos o
 * miles de km de la zona elegida — forzar el mapa a abarcar esa distancia da
 * un zoom ilegible (país entero, sin calles ni referencias). La distancia
 * real de esos resultados se ve igual en la lista; el mapa no necesita
 * "llegar" hasta ellos.
 */
const MAX_MAP_FIT_KM = 300;

export function focusMapOnResults(
  map: google.maps.Map,
  items: ProfessionalWithCoords[],
  userCenter?: Coords | null
) {
  // Si hay un centro de cercanía (localidad/zona elegida o GPS), encuadramos el
  // mapa en ese punto + los resultados más cercanos a vos, en vez del grueso
  // global de la provincia. Así el mapa acompaña al orden por cercanía.
  if (userCenter) {
    const nearest = [...items]
      .sort((a, b) => squaredDistance(a.validCoords, userCenter) - squaredDistance(b.validCoords, userCenter))
      .slice(0, 15);

    const withinFitRange = nearest.filter(
      (item) => haversineKm(userCenter, item.validCoords) <= MAX_MAP_FIT_KM
    );

    if (withinFitRange.length === 0) {
      map.setCenter(userCenter);
      map.setZoom(13);
      return nearest;
    }

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(userCenter);
    withinFitRange.forEach((item) => bounds.extend(item.validCoords));
    map.fitBounds(bounds, 64);

    google.maps.event.addListenerOnce(map, 'idle', () => {
      clampMapZoom(map);
    });

    return nearest;
  }

  const scopedItems = filterMapOutliers(items);

  if (scopedItems.length === 0) {
    map.setCenter({ lat: -34.6037, lng: -58.3816 });
    map.setZoom(12);
    return scopedItems;
  }

  if (scopedItems.length === 1) {
    map.setCenter(scopedItems[0].validCoords);
    map.setZoom(15);
    return scopedItems;
  }

  const clusterCenter = getMapClusterCenter(scopedItems);
  if (!clusterCenter) return scopedItems;

  if (shouldUseFixedCityView(scopedItems)) {
    map.setCenter(clusterCenter);
    map.setZoom(13);
    return scopedItems;
  }

  const bounds = new google.maps.LatLngBounds();
  scopedItems.forEach((item) => bounds.extend(item.validCoords));
  map.fitBounds(bounds, 48);

  google.maps.event.addListenerOnce(map, 'idle', () => {
    clampMapZoom(map);
  });

  return scopedItems;
}
